import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getApprovalQueue, getPendingRejections, getPastDueMilestoneProjects } from "../services/api";

interface RejectedWeek {
    timesheetId: number;
    periodStart: string;
    periodEnd: string;
}

interface PastDueProject {
    projectId: number;
    projectName: string;
}

const formatDate = (iso: string): string => {
    const d = new Date(iso + "T00:00:00");
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

const noticeStyle: React.CSSProperties = {
    color: "#7F6000", backgroundColor: "#FFF2CC", border: "1px solid #F18E00",
    borderRadius: "6px", padding: "10px 14px", marginBottom: "10px", fontSize: "14px",
    cursor: "pointer",
};

const Home: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();

    // Submit/approve actions elsewhere navigate here with a flashMessage in
    // location.state instead of setting local component state right before
    // unmounting -- a message set the instant before navigate() fires never
    // has a chance to render, which was exactly why submit/approval actions
    // looked like they failed silently even on success. Clear the state
    // (via replace, not push) shortly after showing it so a page refresh
    // doesn't re-show a stale success message.
    const [flashMessage, setFlashMessage] = useState<string | null>(
        (location.state as any)?.flashMessage || null
    );
    useEffect(() => {
        if (!flashMessage) return;
        navigate(".", { replace: true, state: {} });
        const t = setTimeout(() => setFlashMessage(null), 4000);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Pending-action notices. Neither of these has a dismiss button -- they
    // reflect live backend state fetched on every visit to Home, so a notice
    // only ever disappears once its underlying condition is actually
    // resolved (approval queue empties out / every rejected line on that
    // week gets fixed and resubmitted), never just by looking at it.
    const [hasApprovalsPending, setHasApprovalsPending] = useState(false);
    const [rejectedWeeks, setRejectedWeeks] = useState<RejectedWeek[]>([]);
    // Past-due milestone notice (2026-08-10 backlog item #3) -- same
    // never-dismissed convention as the other two notices above: it only
    // clears once the milestone is actually invoiced or its Exp Inv Date is
    // pushed out. A plain consultant is never the PM of anything, so this
    // always comes back empty for them -- safe to call unconditionally.
    const [pastDueProjects, setPastDueProjects] = useState<PastDueProject[]>([]);

    useEffect(() => {
        // A plain consultant is simply nobody's approver, so this always
        // comes back empty for them -- safe to call unconditionally rather
        // than gating on role client-side.
        getApprovalQueue("pm")
            .then(data => setHasApprovalsPending(Array.isArray(data) && data.length > 0))
            .catch(() => setHasApprovalsPending(false));

        getPastDueMilestoneProjects()
            .then(data => {
                const projects: any[] = data?.projects || [];
                setPastDueProjects(
                    projects.map(p => ({ projectId: p.project_id, projectName: p.project_name }))
                );
            })
            .catch(() => setPastDueProjects([]));

        // One notice per distinct week, not per rejected line -- collapse
        // the line-level list from the backend down to its unique
        // (timesheet_id / period) groups, so a week with several rejected
        // lines still only produces a single message.
        getPendingRejections()
            .then(data => {
                const lines: any[] = data?.pending_rejections || [];
                const byTimesheet = new Map<number, RejectedWeek>();
                for (const line of lines) {
                    if (!byTimesheet.has(line.timesheet_id)) {
                        byTimesheet.set(line.timesheet_id, {
                            timesheetId: line.timesheet_id,
                            periodStart: line.period_start,
                            periodEnd: line.period_end,
                        });
                    }
                }
                setRejectedWeeks(
                    Array.from(byTimesheet.values())
                        .sort((a, b) => a.periodStart.localeCompare(b.periodStart))
                );
            })
            .catch(() => setRejectedWeeks([]));
    }, []);

    return (
        <div>
            {flashMessage && (
                <div style={{
                    backgroundColor: "#E8F5E9", border: "1px solid #2E7D32", color: "#2E7D32",
                    borderRadius: "6px", padding: "10px 14px", marginBottom: "16px", fontSize: "14px",
                }}>
                    ✓ {flashMessage}
                </div>
            )}

            {hasApprovalsPending && (
                <div onClick={() => navigate("/approvals")} style={noticeStyle}>
                    ⚠ You have pending items in your Approval Queue.
                </div>
            )}

            {rejectedWeeks.map(week => (
                <div
                    key={week.timesheetId}
                    onClick={() => navigate(`/timesheets?period_start=${week.periodStart}`)}
                    style={noticeStyle}
                >
                    ⚠ You have a rejected timesheet from {formatDate(week.periodStart)} that requires action.
                </div>
            ))}

            {pastDueProjects.map(p => (
                <div
                    key={p.projectId}
                    onClick={() => navigate(`/reports/project-info/${p.projectId}`)}
                    style={noticeStyle}
                >
                    ⚠ Project {p.projectName} has a past due milestone.
                </div>
            ))}

            <h1 style={{ color: "#5C5D5E", marginTop: 0 }}>Welcome to Amplify Projects & Financials</h1>
            <p style={{ color: "#666" }}>Select an option from the menu to get started.</p>
        </div>
    );
};

export default Home;
